/**
 * Upload an image asset to Supabase storage.
 * Used by CompleteProfileScreen and RegisterScreen for profile pics and evidence docs.
 * @param {object} supabase - Supabase client
 * @param {string} bucket - Storage bucket name
 * @param {object} imageAsset - From ImagePicker or pickFileWeb: { uri, base64, file, arrayBuffer }
 * @param {string} pathPrefix - e.g. 'userId/profile' or 'userId/evidence-0'
 * @param {function} onError - Optional (message) => void to show toast
 * @param {function} getPublicUrl - (bucket, path) => url
 */
export async function uploadImageToStorage(supabase, bucket, imageAsset, pathPrefix, onError, getPublicUrl) {
  if (!imageAsset) return null;
  try {
    const timestamp = Date.now();
    const filePath = `${pathPrefix}-${timestamp}.jpg`;
    let body;
    let contentType = 'image/jpeg';

    if (imageAsset.file) {
      body = imageAsset.file;
      contentType = imageAsset.file.type || 'image/jpeg';
    } else if (imageAsset.arrayBuffer) {
      body = new Uint8Array(imageAsset.arrayBuffer);
    } else if (imageAsset.base64) {
      const byteString = atob(imageAsset.base64);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      body = new Blob([ab], { type: 'image/jpeg' });
    } else if (imageAsset.uri) {
      const uri = imageAsset.uri;
      if (uri.startsWith('data:')) {
        const [header, b64] = uri.split(',');
        const mime = (header.match(/:(.*?);/) || [])[1] || 'image/jpeg';
        const byteString = atob(b64);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
        body = new Blob([ab], { type: mime });
        contentType = mime;
      } else {
        const resp = await fetch(uri);
        if (!resp.ok) throw new Error(`Fetch failed: ${resp.status}`);
        body = await resp.blob();
        contentType = body.type || 'image/jpeg';
      }
    } else {
      throw new Error('No image data');
    }

    const { error } = await supabase.storage.from(bucket).upload(filePath, body, {
      cacheControl: '3600',
      upsert: true,
      contentType,
    });
    if (error) throw error;
    const url = getPublicUrl(bucket, filePath);
    return { url, path: filePath };
  } catch (e) {
    console.error(`Upload to ${bucket} failed:`, e);
    if (onError) onError(e?.message || 'Upload failed');
    return null;
  }
}
